import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { isBatchManaged } from "@/lib/playerBatchRegistry";

const SESSION_CACHE_KEY = "player_avatar_cache";
const CACHE_EXPIRY_MS = 6 * 60 * 60 * 1000; // 6 hours

interface AvatarCacheEntry {
  url: string;
  timestamp: number;
}

interface AvatarCache {
  [key: string]: AvatarCacheEntry;
}

const loadSessionCache = (): AvatarCache => {
  try {
    const cached = sessionStorage.getItem(SESSION_CACHE_KEY);
    if (!cached) return {};
    
    const data: AvatarCache = JSON.parse(cached);
    const now = Date.now();
    
    const validCache: AvatarCache = {};
    for (const [key, entry] of Object.entries(data)) {
      if (now - entry.timestamp < CACHE_EXPIRY_MS) {
        validCache[key] = entry;
      }
    }
    
    if (Object.keys(validCache).length !== Object.keys(data).length) {
      sessionStorage.setItem(SESSION_CACHE_KEY, JSON.stringify(validCache));
    }
    
    return validCache;
  } catch {
    return {};
  }
};

const saveToSessionCache = (key: string, url: string): void => {
  try {
    const cache = loadSessionCache();
    cache[key] = { url, timestamp: Date.now() };
    sessionStorage.setItem(SESSION_CACHE_KEY, JSON.stringify(cache));
  } catch {
    // Storage quota exceeded
  }
};

const getFromSessionCache = (key: string): string | null => {
  const cache = loadSessionCache();
  const entry = cache[key];
  
  if (!entry) return null;
  
  if (Date.now() - entry.timestamp >= CACHE_EXPIRY_MS) {
    delete cache[key];
    sessionStorage.setItem(SESSION_CACHE_KEY, JSON.stringify(cache));
    return null;
  }
  
  return entry.url;
};

// Check Supabase backend with maybeSingle
const fetchFromBackend = async (uuid: string, size: number): Promise<string | null> => {
  try {
    const { data: userRecord, error } = await (supabase as any)
      .from('api_gameusers')
      .select('avatar_url, updated_at')
      .eq('uuid', uuid)
      .maybeSingle();

    if (error || !userRecord) return null;

    const updatedAt = new Date(userRecord.updated_at);
    const sixHoursAgo = new Date(Date.now() - CACHE_EXPIRY_MS);

    if (updatedAt > sixHoursAgo && userRecord.avatar_url) {
      return userRecord.avatar_url;
    }

    return null;
  } catch {
    return null;
  }
};

const fetchFromEdgeFunction = async (uuid: string, size: number): Promise<string | null> => {
  try {
    const { data, error } = await supabase.functions.invoke('gameuser-cache', {
      body: { uuid, size }
    });

    if (error) return null;
    return data?.avatar_url || null;
  } catch {
    return null;
  }
};

const fetchPlayerAvatar = async (uuid: string, size: number = 16): Promise<string> => {
  const cacheKey = `${uuid}_${size}`;
  const fallbackUrl = `https://mc-api.io/render/${uuid}?size=${size}`;

  // 1. Check sessionStorage cache
  const sessionCached = getFromSessionCache(cacheKey);
  if (sessionCached) {
    return sessionCached;
  }

  // 2. Check Supabase backend/storage cache
  const backendUrl = await fetchFromBackend(uuid, size);
  if (backendUrl) {
    saveToSessionCache(cacheKey, backendUrl);
    return backendUrl;
  }

  // 3. Fetch from API via Edge Function
  const avatarUrl = await fetchFromEdgeFunction(uuid, size);
  
  if (avatarUrl) {
    saveToSessionCache(cacheKey, avatarUrl);
    return avatarUrl;
  }
  
  return fallbackUrl;
};

export const usePlayerAvatar = (uuid: string, size: number = 16) => {
  const cacheKey = `${uuid}_${size}`;
  const sessionCached = getFromSessionCache(cacheKey);
  
  return useQuery({
    queryKey: ['playerAvatar', cacheKey],
    queryFn: () => fetchPlayerAvatar(uuid, size),
    staleTime: CACHE_EXPIRY_MS,
    gcTime: CACHE_EXPIRY_MS,
    retry: 1,
    refetchOnWindowFocus: false,
    initialData: () => sessionCached ?? undefined,
    // Skip individual fetch when batch system is handling this UUID
    enabled: !isBatchManaged(uuid) || !!sessionCached,
  });
};
